/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import PropTypes from 'prop-types';
import { Camera, FileText, Music, Video } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';
import CaptureHeader from '../CaptureHeader';
import GlobalInstructionButton from './GlobalInstructionButton';
import Modal from '../Modal';
import NotesButton from './NotesButton';
import NotesSummary from './NotesSummary';
import './capture.css';
import ReferenceTooltip from './ReferenceTooltip';
import { DEFAULT_CUSTOMER_REFERENCE_LABEL, resolveCustomerReferenceLabel } from '../../utils/customerReferenceLabel';
import { getMediaCategory, isBrowserNativeVideo } from '../../constants/mimeMap';
import fileAttachedPin from '../../assets/file-attached-pin.svg';
import PhotoGallery from './PhotoGallery';
import FullScreenGallery from './FullScreenGallery';
import useCachedAssetSrc from '../../hooks/useCachedAssetSrc';
import SyncStatusToast from '../SyncStatusToast';

const SYNC_BLOCKED_MESSAGE = 'Before proceeding, you need to sync the data.';

// Shared capture-action button styling — kept identical to MultiPhotoReview.jsx
const ACTION_BTN_BASE = 'h-[42px] px-3 font-bold text-[17px] !px-8 rounded-[8px] border-2 active:scale-[0.98] transition-all shadow-md flex items-center justify-center whitespace-nowrap';
const ACTION_BTN_RETAKE = `${ACTION_BTN_BASE} bg-black text-white border-black`;
const ACTION_BTN_DELETE = `${ACTION_BTN_BASE} bg-error text-error-content border-error disabled:opacity-50 disabled:cursor-not-allowed`;
const ACTION_BTN_ADD = `${ACTION_BTN_BASE} bg-primary text-primary-content border-primary`;

// Determine effective rendering category for an item.
// For 'file' type items, use mimeType + fileName (handles application/octet-stream fallback).
// For native types, pass through directly.
const getEffectiveCategory = (type, mimeType, fileName) => {
  if (type === 'file') return getMediaCategory(mimeType, fileName);
  return type; // 'photo' | 'video' | 'identifier'
};

export const FullScreenMediaSlide = ({ url, type, mimeType, fileName, token }) => {
  const effectiveCategory = getEffectiveCategory(type, mimeType, fileName);
  const [videoError, setVideoError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [audioError, setAudioError] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const hasResolvedDuration = useRef(false);
  // Shared across image/video/audio below — the live URL is tried first,
  // with a fallback to the cached blob (see assetCache) on load failure so
  // this stays viewable offline for every previewable media type, not just
  // images.
  const { src: mediaSrc, tryFallback: tryMediaFallback } = useCachedAssetSrc(token, url);

  if (effectiveCategory === 'video') {
    return videoError ? (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
        <Video size={36} className="text-base-content/30" strokeWidth={1.5} />
        <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this video type</p>
      </div>
    ) : (
      <>
        {videoLoading && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1 }}>
            <div className="w-10 h-10 border-4 border-white/20 border-t-white rounded-full animate-spin" />
          </div>
        )}
        <video
          onLoadedMetadata={(e) => {
            const v = e.target;
            if (!Number.isFinite(v.duration)) {
              v.currentTime = Number.MAX_SAFE_INTEGER;
            }
          }}
          onDurationChange={(e) => {
            const v = e.target;
            if (!hasResolvedDuration.current && Number.isFinite(v.duration)) {
              hasResolvedDuration.current = true;
              v.currentTime = 0;
            }
          }}
          onCanPlayThrough={(e) => { e.target.style.opacity = 1; setVideoLoading(false); }}
          src={mediaSrc}
          controls
          autoPlay
          playsInline
          onClick={e => e.stopPropagation()}
          style={{ width: '100%', height: '100%', objectFit: 'contain', opacity: 0, transition: 'opacity 0.2s' }}
          onError={async () => {
            const usedFallback = await tryMediaFallback();
            if (!usedFallback) setVideoError(true);
          }}
        />
      </>
    );
  }

  if (effectiveCategory === 'image' || effectiveCategory === 'photo' || effectiveCategory === 'identifier') {
    return imageError ? (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
        <Camera size={36} className="text-base-content/30" strokeWidth={1.5} />
        <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this image type</p>
      </div>
    ) : (
      <img
        src={mediaSrc}
        alt="Full view"
        onClick={e => e.stopPropagation()}
        onError={async () => {
          const usedFallback = await tryMediaFallback();
          if (!usedFallback) setImageError(true);
        }}
        style={{ width: '100%', height: '100%', objectFit: 'contain' }}
      />
    );
  }

  return (
    <div className="text-white w-full h-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
      {effectiveCategory === 'audio' ? (
        audioError ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
            <Music size={36} className="text-base-content/30" strokeWidth={1.5} />
            <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Browser does not support this audio type</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
            <Music size={64} strokeWidth={1} />
            <audio
              controls
              preload="metadata"
              src={mediaSrc}
              onError={async () => {
                const usedFallback = await tryMediaFallback();
                if (!usedFallback) setAudioError(true);
              }}
            />
            <p className="text-sm opacity-60">{mimeType || fileName}</p>
          </div>
        )
      ) : effectiveCategory === 'document' ? (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
          <FileText size={36} className="text-base-content/30" strokeWidth={1.5} />
          <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Preview not available for this file type</p>
        </div>
      ) : (
        <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
          <FileText size={36} className="text-base-content/30" strokeWidth={1.5} />
          <p className="text-[10px] font-bold text-base-content/30 uppercase tracking-widest custom-line-break">Unsupported file type</p>
        </div>
      )}
    </div>
  );
};

FullScreenMediaSlide.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  mimeType: PropTypes.string,
  fileName: PropTypes.string,
  token: PropTypes.string,
};

const MediaCard = ({ url, type, duration, mimeType, fileName, onError, token }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const videoRef = useRef(null);

  const effectiveCategory = getEffectiveCategory(type, mimeType, fileName);
  // Shared across image/video/audio below — the live URL is tried first,
  // with a fallback to the cached blob (see assetCache) on load failure so
  // this stays viewable offline for every previewable media type, not just
  // images.
  const { src: mediaSrc, tryFallback: tryMediaFallback } = useCachedAssetSrc(token, url);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  const handleLoad = () => { setLoading(false); };
  const handleVideoCanPlay = () => { setLoading(false); };
  // Shared by video/audio/image below — live URL failed, so try the cached
  // blob before falling back to the "unsupported" placeholder.
  const handleMediaError = async () => {
    const usedFallback = await tryMediaFallback();
    if (!usedFallback) { setError(true); onError?.(); }
  };

  // For iOS: Force load on mount, and again if the src swaps to a cached
  // blob after a live-load failure (see handleMediaError above).
  useEffect(() => {
    if (effectiveCategory === 'video' && videoRef.current) {
      videoRef.current.load();
    }
  }, [effectiveCategory, mediaSrc]);

  useEffect(() => {
    const handlePageShow = (event) => {
      if (event.persisted) {
        window.location.reload();
      }
    };

    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, []);

  // Document — always show extension placeholder, no preview, no error
  if (effectiveCategory === 'document') {
    return (
      <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
        <img
          src={fileAttachedPin}
          alt="attachment"
          className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
        />
        <span className="text-[18px] md:text-[20px] text-base-content truncate">
          {fileName || 'file'}
        </span>
      </div>
    );
  }

  // Non-native video format (e.g. FLV, WMV, AVI, MKV) — always show placeholder directly
  // Exception: if the URL points to a transcoded .mp4, treat it as native
  const isTranscodedMp4 = effectiveCategory === 'video' && /\.mp4(\?|$)/i.test(url || '');
  if (effectiveCategory === 'video' && !isBrowserNativeVideo(mimeType, fileName) && !isTranscodedMp4) {
    return (
      <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
        <img
          src={fileAttachedPin}
          alt="attachment"
          className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
        />
        <span className="text-[18px] md:text-[20px] text-base-content truncate">
          {fileName || 'file'}
        </span>
      </div>
    );
  }

  // Audio — attempt to render, show error only if browser can't play it
  if (effectiveCategory === 'audio') {
    if (error) {
      return (
        <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
          <img
            src={fileAttachedPin}
            alt="attachment"
            className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
          />
          <span className="text-[18px] md:text-[20px] text-base-content truncate">
            {fileName || 'file'}
          </span>
        </div>
      );
    }
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-base-200">
        <audio
          controls
          preload="metadata"
          src={mediaSrc}
          onError={handleMediaError}
        />
      </div>
    );
  }

  return (
    <>
      {loading && !error && (
        <div className="absolute inset-0 flex items-center justify-center bg-base-200">
          <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {effectiveCategory === 'video' ? (
        error ? (
          <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
            <img
              src={fileAttachedPin}
              alt="attachment"
              className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
            />
            <span className="text-[18px] md:text-[20px] text-base-content truncate">
              {fileName || 'file'}
            </span>
          </div>
        ) : (
          <video
            ref={videoRef}
            src={mediaSrc}
            className="w-full h-full object-contain"
            onCanPlay={handleVideoCanPlay}
            onLoadedData={handleVideoCanPlay}
            onError={handleMediaError}
            muted
            playsInline
            preload="auto"
            poster=""
            style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }}
          />
        )
      ) : (
        error ? (
          <div className="w-full h-full flex items-center justify-center gap-2 bg-file-unsupported px-4 select-none">
            <img
              src={fileAttachedPin}
              alt="attachment"
              className="w-[42px] h-[42px] shrink-0 theme-invert-icon"
            />
            <span className="text-[18px] md:text-[20px] text-base-content truncate">
              {fileName || 'file'}
            </span>
          </div>
        ) : (
          <img
            src={mediaSrc}
            alt="capture"
            className="w-full h-full object-contain mx-auto"
            onLoad={handleLoad}
            onError={handleMediaError}
            style={{ opacity: loading ? 0 : 1, transition: 'opacity 0.3s' }}
          />
        )
      )}
    </>
  );
};

MediaCard.propTypes = {
  url: PropTypes.string,
  type: PropTypes.string,
  mimeType: PropTypes.string,
  fileName: PropTypes.string,
  onError: PropTypes.func,
  duration: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  token: PropTypes.string,
};

export const ReviewItem = ({ item, index, capturedFiles, serverCompleted, completedItemsMedia, onRetake, onDelete, onDeletePhoto, onAddPhoto, setViewer, setDeleteModal, downloadingItems, readOnly = false, token }) => {
  const isCaptured = !!(capturedFiles[item.assignmentItemId] || serverCompleted[item.assignmentItemId]);
  const isSkipped = item.status === 'skipped';
  const localPhotos = capturedFiles[item.assignmentItemId]?.photos || [];
  const serverPhotos = Array.isArray(completedItemsMedia[item.assignmentItemId]) ? completedItemsMedia[item.assignmentItemId] : [];
  const photos = localPhotos.length > 0 ? localPhotos : serverPhotos;
  const primaryPhoto = photos[0];
  const url = primaryPhoto?.url || primaryPhoto?.previewUrl || primaryPhoto?.truVideoUrl;
  const duration = primaryPhoto?.duration || 0;
  const mimeType = primaryPhoto?.mimeType || primaryPhoto?.file?.type || null;
  const fileName = primaryPhoto?.fileName || primaryPhoto?.file?.name || null;
  const effectiveCategory = getEffectiveCategory(item.type, mimeType, fileName);
  const [mediaError, setMediaError] = useState(false);
  const isTranscodedMp4 = effectiveCategory === 'video' && /\.mp4(\?|$)/i.test(url || '');
 
  const [activePhotoIndex, setActivePhotoIndex] = useState(0);
  const [deletingPhoto, setDeletingPhoto] = useState(false);
  const isNativeVideo = effectiveCategory === 'video' && (isBrowserNativeVideo(mimeType, fileName) || isTranscodedMp4);
  const identifierText = primaryPhoto?.customerIdentifierText ?? primaryPhoto?.identifierText ?? '';
  const maxPhotos = Number(item.maxPhotos ?? 1);
  const canAddMorePhotos = item.type === 'photo' && maxPhotos > 1 && photos.length < maxPhotos;
  const canPreview = (
    effectiveCategory === 'photo' ||
    effectiveCategory === 'identifier' ||
    effectiveCategory === 'image' ||
    isNativeVideo
  ) && !mediaError;
  // True only for photo-type steps that allow and have multiple photos
  const isMultiPhoto = item.type === 'photo' && maxPhotos > 1 && photos.length > 1;
  const showMultiPhotoActions = item.type === 'photo' && maxPhotos > 1 && photos.length > 0;

  // Clamp activePhotoIndex when photos array shrinks (e.g. after a deletion)
  useEffect(() => {
    if (photos.length > 0 && activePhotoIndex >= photos.length) {
      setActivePhotoIndex(photos.length - 1);
    }
  }, [photos.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="animate-fade-in">
      {/* Step Label */}
      <div className="mb-4">
        <div className="flex items-start gap-3 mb-2">
          <span className="text-[16px] font-black text-base-content whitespace-nowrap min-w-fit">Step {index + 1}</span>
          <span className="text-[16px] font-semibold text-base-content custom-line-break flex-1">
            {item.description}
            {item.required ? <span className="text-error ml-1 font-bold">*</span> : (<span><span className="ml-1 text-[14px] font-normal text-base-content/60"> (Optional)</span></span>)}
          </span>
        </div>
        {item.type === 'identifier' && identifierText && (
          <div className="bg-primary/10 border-primary/20 border rounded-xl text-primary px-2 py-1 text-xs font-medium text-primary-700 inset-ring inset-ring-primary-600/10">
            <span className="text-[12px] font-semibold uppercase custom-line-break">{identifierText}</span>
          </div>
        )}
      </div>

      <div className="relative rounded-[12px] overflow-hidden bg-black aspect-video border border-base-300 shadow-lg">
        {url ? (
          <div className="w-full h-full relative">
            {isMultiPhoto ? (
              /* ── Multi-photo: swipeable gallery ── */
              <PhotoGallery
                photos={photos}
                activeIndex={activePhotoIndex}
                onIndexChange={setActivePhotoIndex}
                renderSlide={(photo) => {
                  const photoUrl = photo?.url || photo?.previewUrl || photo?.truVideoUrl;
                  const photoMimeType = photo?.mimeType || photo?.file?.type || null;
                  const photoFileName = photo?.fileName || photo?.file?.name || null;
                  const photoDuration = photo?.duration || 0;
                  return (
                    <MediaCard
                      key={photoUrl}
                      url={photoUrl}
                      type={item.type}
                      duration={photoDuration}
                      mimeType={photoMimeType}
                      fileName={photoFileName}
                      onError={() => setMediaError(true)}
                      token={token}
                    />
                  );
                }}
                canPreview={canPreview}
                onPreview={() => {
                  setViewer({
                    items: photos.map((photo) => ({
                      url: photo?.url || photo?.previewUrl || photo?.truVideoUrl,
                      mimeType: photo?.mimeType || photo?.file?.type || null,
                      fileName: photo?.fileName || photo?.file?.name || null,
                    })),
                    type: item.type,
                    initialIndex: activePhotoIndex,
                    onIndexChange: setActivePhotoIndex,
                  });
                }}
              />
            ) : (
              /* ── Single photo: unchanged behaviour ── */
              <div
                className="w-full h-full cursor-pointer group/media"
                onClick={() => {
                  if (canPreview) {
                    setViewer({ items: [{ url, mimeType, fileName }], type: item.type, initialIndex: 0 });
                  }
                }}
              >
                <MediaCard key={url} url={url} type={item.type} duration={duration} mimeType={mimeType} fileName={fileName} onError={() => setMediaError(true)} token={token} />
                {canPreview && (
                  <div className="absolute inset-0 bg-black/5 flex items-center justify-center">
                    <div className="bg-primary/90 backdrop-blur-md px-4 py-2 rounded-full text-primary-content font-black text-[10px] uppercase tracking-widest shadow-lg">Tap to preview</div>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : isSkipped ? (
          <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-center p-8 bg-base-200/50">
            <div className="w-16 h-16 rounded-full bg-base-content/5 flex items-center justify-center text-base-content/30">
              <Camera size={28} strokeWidth={1.5} />
            </div>
            <p className="text-[13px] font-black text-base-content/40 uppercase tracking-widest">Step Skipped</p>
            {!readOnly && (
              <button onClick={() => onRetake(item.assignmentItemId)} className="bg-primary text-primary-content text-[13px] font-black px-7 py-3 rounded-lg uppercase tracking-widest shadow-lg active:scale-95 transition-transform">{item.type === 'file' ? "Upload Now" : "Capture Now"}</button>
            )}
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-4 text-center p-8 bg-base-200">
            <Camera size={36} strokeWidth={1.5} className="text-base-content/30" />
            <p className="text-[13px] font-black text-base-content/40 uppercase tracking-widest">No Capture Yet</p>
            {!readOnly && (
              <button onClick={() => onRetake(item.assignmentItemId)} className="bg-primary text-primary-content text-[13px] font-black px-7 py-3 rounded-lg uppercase tracking-widest shadow-lg active:scale-95 transition-transform">Capture Now</button>
            )}
          </div>
        )}
      </div>

      {isCaptured && !readOnly && (
        <div className="mt-3 space-y-2">
          {/* Multi-photo: caption for the currently visible slide */}
          {isMultiPhoto && photos.length > 0 && (
            <p className="text-center text-[13px] text-base-content/60">
              Photo <span className="font-bold text-base-content">{activePhotoIndex + 1}</span> of <span className="font-bold text-base-content">{photos.length}</span>
            </p>
          )}
          <div className="flex gap-3 justify-center">
            {/* Multi-photo: Retake / Delete operate on the currently visible slide */}
            {showMultiPhotoActions && (
              <>
                <button
                  type="button"
                  onClick={() => onRetake(item.assignmentItemId, true, photos[activePhotoIndex]?.mediaFileId, activePhotoIndex)}
                  className={ACTION_BTN_RETAKE}
                >
                  Retake
                </button>
                {(photos.length > 1 || !item.required) && (
                  <button
                    type="button"
                    onClick={async () => {
                      if (deletingPhoto) return;
                      setDeletingPhoto(true);
                      try {
                        await onDeletePhoto?.(item.assignmentItemId, photos[activePhotoIndex]?.mediaFileId, photos[activePhotoIndex]?.localId);
                      } finally {
                        setDeletingPhoto(false);
                      }
                    }}
                    disabled={deletingPhoto}
                    className={ACTION_BTN_DELETE}
                  >
                    {deletingPhoto ? (
                      <span className="w-4 h-4 border-2 border-error-content/40 border-t-error-content rounded-full animate-spin" />
                    ) : (
                      'Delete'
                    )}
                  </button>
                )}
              </>
            )}
            {canAddMorePhotos && (
              <button
                onClick={() => onAddPhoto?.(item.assignmentItemId)}
                className={ACTION_BTN_ADD}
              >
                Add
              </button>
            )}
            {/* Hide general Retake button for multi-photo steps — use per-photo retake instead */}
            {maxPhotos === 1 && (
              <button
                onClick={() => onRetake(item.assignmentItemId)}
                className={ACTION_BTN_RETAKE}
              >
                {item.type === 'file' ? 'Replace' : 'Retake'}
              </button>
            )}
            {!item.required && maxPhotos === 1 && (
              <button
                onClick={() => setDeleteModal({ isOpen: true, itemId: item.assignmentItemId, itemDescription: item.description })}
                className={ACTION_BTN_DELETE}
              >
                Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

ReviewItem.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.string,
    description: PropTypes.string,
    required: PropTypes.bool,
    status: PropTypes.string,
  }),
  index: PropTypes.number,
  capturedFiles: PropTypes.object,
  serverCompleted: PropTypes.object,
  completedItemsMedia: PropTypes.object,
  onRetake: PropTypes.func,
  onDelete: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onAddPhoto: PropTypes.func,
  setViewer: PropTypes.func,
  setDeleteModal: PropTypes.func,
  downloadingItems: PropTypes.object,
  token: PropTypes.string,
  readOnly: PropTypes.bool,
};

const Review = ({
  items,
  capturedFiles,
  serverCompleted,
  completedItemsMedia,
  branding,
  workflow,
  assignment,
  submitError,
  onRetake,
  onDelete,
  onDeletePhoto,
  onSubmit,
  onStart,
  onAddPhoto,
  token,
  onOpenNotes,
  notes = [],
  onAddNote,
  syncBlockedNotice = false,
  onDismissSyncBlockedNotice,
  syncStatus = null,
  onDismissSyncStatus,
}) => {
  const [viewer, setViewer] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);
  const tooltipTriggerRef = useRef(null);
  const nameTooltipTriggerRef = useRef(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, itemId: null, itemDescription: '' });
  const [downloadingItems, setDownloadingItems] = useState({});

  const currentTheme = branding.theme || 'light';
  const dynamicCustomerReferenceLabel = resolveCustomerReferenceLabel(workflow);

  // Scroll to top when a submit error appears so the user sees the error banner immediately
  useEffect(() => {
    if (submitError) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [submitError]);

  const requiredItems = items.filter(i => i.required);
  const allRequiredCaptured = requiredItems.every((item) => {
    const localEntry = capturedFiles[item.assignmentItemId];
    const photoCount = localEntry?.photos?.length || 0;
    const hasServerPhotos = Array.isArray(completedItemsMedia[item.assignmentItemId]) && completedItemsMedia[item.assignmentItemId].length > 0;
    return serverCompleted[item.assignmentItemId] || photoCount > 0 || hasServerPhotos;
  });

  return (
    <div data-theme={currentTheme} className="min-h-screen flex flex-col bg-base-100 text-base-content overflow-x-hidden capture-container landscape-root">
      {viewer && (
        <FullScreenGallery
          items={viewer.items}
          initialIndex={viewer.initialIndex}
          onClose={() => setViewer(null)}
          onIndexChange={viewer.onIndexChange}
          renderItem={(slide) => (
            <FullScreenMediaSlide
              key={slide.url}
              url={slide.url}
              type={viewer.type}
              mimeType={slide.mimeType}
              fileName={slide.fileName}
              token={token}
            />
          )}
        />
      )}

      {/* ── LEFT SIDEBAR ── */}
      <div className="landscape-sidebar landscape-review-sidebar w-full">
        {/* Notch Spacer */}
        <div className="h-[env(safe-area-inset-top,20px)]" />

        {/* Header (sticky in portrait) */}
        <div className="w-full bg-base-100/95 backdrop-blur-sm p-3 pt-0">
          <div className="w-full max-w-full mx-auto text-left">
            {/* CaptureHeader Integration */}
            <CaptureHeader expiresAt={assignment?.expiresAt} brandLogo={workflow.branding?.logoUrl} branding={branding} className="pt-0 pb-0" type='after-capture' token={token} />

            <div className="flex flex-col gap-1 mt-1">
              <div className="flex justify-between items-start gap-2">
                <div className="relative min-w-0">
                  <h2
                    ref={nameTooltipTriggerRef}
                    className="text-[20px] font-bold text-base-content leading-none tracking-tight truncate cursor-pointer transition-opacity hover:opacity-80 active:opacity-70"
                    onClick={() => setShowNameTooltip(!showNameTooltip)}
                  >
                    {assignment.submitterName || assignment.workflowName || 'Mobile Capture'}
                  </h2>
                  <ReferenceTooltip
                    assignment={assignment}
                    workflow={workflow}
                    show={showNameTooltip}
                    onClose={() => setShowNameTooltip(false)}
                    theme={currentTheme}
                    triggerRef={nameTooltipTriggerRef}
                    type="name"
                    className="tooltip-for-landscap-review"
                  />
                </div>
                {onOpenNotes && <NotesButton onClick={onOpenNotes} />}
              </div>
              <div className="shrink max-w-full overflow-visible text-[14px]">
                <div className="relative flex flex-wrap justify-between items-start custom-line-break w-full">
                  <span>{dynamicCustomerReferenceLabel === DEFAULT_CUSTOMER_REFERENCE_LABEL ? 'Reference' : dynamicCustomerReferenceLabel}:{" "}</span>
                  <span className="inline-block text-right shrink max-w-full break-all">
                    <button
                      type="button"
                      ref={tooltipTriggerRef}
                      onClick={() => setShowTooltip(!showTooltip)}
                      className="focus:outline-none underline decoration-dashed text-primary custom-line-break"
                    >
                      {assignment.customerReferenceNumber}
                    </button>
                  </span>
                  <ReferenceTooltip
                    assignment={assignment}
                    workflow={workflow}
                    show={showTooltip}
                    onClose={() => setShowTooltip(false)}
                    theme={currentTheme}
                    triggerRef={tooltipTriggerRef}
                    type="reference"
                    className="tooltip-for-landscap-review"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Portrait intro text (hidden in landscape) */}
        <div className="flex-1 p-3 w-full max-w-full mx-auto landscape-portrait-review-intro">
          <div>
            <h1 className="text-[20px] font-bold text-base-content mb-2 tracking-tight">Check your media before submitting</h1>
            <p className="text-[14px] leading-tight text-base-content">
              Review each item below. You can retake or remove any capture before submitting your media.
            </p>
          </div>

          {submitError && (
            <div className="mt-3 p-4 bg-error/10 border border-error/20 rounded-xl text-[13px] font-bold text-error flex items-center gap-3">
              <div className="w-2 h-2 bg-error rounded-full shrink-0" />
              {submitError}
            </div>
          )}
        </div>
        <div className="landscape-sidebar-action hidden flex-col p-3">
          <h1 className="text-[18px] font-bold text-base-content mb-1 tracking-tight">Check your media</h1>
          <p className="text-[13px] leading-tight text-base-content/70">
            Review each item below. You can retake or remove any capture before submitting your media.
          </p>
        </div>
        {/* Landscape: intro text + submit button inside sidebar */}
        <div className="landscape-sidebar-action hidden flex-col gap-3 p-3">

          {submitError && (
            <div className="p-3 bg-error/10 border border-error/20 rounded-xl text-[12px] font-bold text-error">
              {submitError}
            </div>
          )}
          {syncBlockedNotice && (
            <SyncStatusToast
              status="error"
              message={SYNC_BLOCKED_MESSAGE}
              onDismiss={onDismissSyncBlockedNotice}
              variant="inline"
            />
          )}
          {syncStatus?.status && (
            <SyncStatusToast
              status={syncStatus.status}
              message={syncStatus.message}
              onDismiss={onDismissSyncStatus}
              variant="inline"
            />
          )}
          <div className="flex items-center gap-3">
            <GlobalInstructionButton globalInstructionUrl={workflow?.globalInstructionUrl} token={token} className="!w-11 !h-11 shrink-0" />
            <button
              onClick={onSubmit}
              disabled={!allRequiredCaptured}
              className={`flex-1 py-2.5 rounded-xl font-semibold text-[17px] transition-all active:scale-[0.98] shadow-xl ${allRequiredCaptured ? 'bg-primary text-primary-content' : 'bg-base-200 text-base-content/30 cursor-not-allowed'}`}
            >
              Send Media
            </button>
          </div>
        </div>
      </div>

      {/* ── RIGHT MAIN PANEL / Portrait full-scroll area ── */}
      <div className="landscape-main flex-1 max-w-full">
        {/* Landscape header for review items */}
        <div className="landscape-review-main-header" style={{ display: 'none' }}>
          <div className="px-4 pt-4 pb-2">
            {submitError && (
              <div className="p-3 bg-error/10 border border-error/20 rounded-xl text-[12px] font-bold text-error flex items-center gap-3">
                <div className="w-2 h-2 bg-error rounded-full shrink-0" />
                {submitError}
              </div>
            )}
          </div>
        </div>

        <div className="p-4 space-y-6 w-full max-w-full mx-auto text-left pb-[140px] landscape-pb-0">
          {onOpenNotes && <NotesSummary notes={notes} onView={onOpenNotes} onAdd={onAddNote} />}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {items.map((item, index) => (
              <div key={item.id}>
                <ReviewItem
                  item={item}
                  index={index}
                  capturedFiles={capturedFiles}
                  serverCompleted={serverCompleted}
                  completedItemsMedia={completedItemsMedia}
                  onRetake={onRetake}
                  onDelete={onDelete}
                  onDeletePhoto={onDeletePhoto}
                  onAddPhoto={onAddPhoto}
                  setViewer={setViewer}
                  setDeleteModal={setDeleteModal}
                  downloadingItems={downloadingItems}
                  token={token}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Portrait: sticky bottom action bar */}
      <div className="fixed bottom-0 left-0 right-0 p-4 px-3 bg-base-100/95 backdrop-blur-sm rounded-t-[20px] z-30 shadow-[0_-4px_10px_rgba(0,0,0,0.1)] border-t border-base-300/50 landscape-hide-fixed-bar">
        <div className="max-w-full mx-auto flex flex-col justify-center gap-10">
          {syncBlockedNotice && (
            <SyncStatusToast
              status="error"
              message={SYNC_BLOCKED_MESSAGE}
              onDismiss={onDismissSyncBlockedNotice}
              variant="inline"
            />
          )}
          {syncStatus?.status && (
            <SyncStatusToast
              status={syncStatus.status}
              message={syncStatus.message}
              onDismiss={onDismissSyncStatus}
              variant="inline"
            />
          )}
          <div className="flex items-center justify-center gap-10">
            <GlobalInstructionButton globalInstructionUrl={workflow?.globalInstructionUrl} token={token} className="!w-11 !h-11 shrink-0" />
            <button
              onClick={onSubmit}
              disabled={!allRequiredCaptured}
              className={`py-2 px-10 rounded-xl font-semibold text-[20px] transition-all active:scale-[0.98] shadow-xl ${allRequiredCaptured ? 'bg-primary text-primary-content' : 'bg-base-200 text-base-content/30 cursor-not-allowed'}`}
            >
              Send Media
            </button>
          </div>
          {/* <button
            onClick={onStart}
            className="flex items-center justify-center gap-2 text-base-content/40 font-bold text-[15px] hover:text-base-content py-1 leading-none transition-colors"
          >
            <ChevronLeft size={20} strokeWidth={2.5} />
            Back to steps
          </button> */}
        </div>
      </div>

      {/* Extra spacing for home indicator */}
      <div className="h-[env(safe-area-inset-bottom,20px)] bg-base-100" />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' })}
        title="Delete Media"
        fontFamily="Roboto, -apple-system, BlinkMacSystemFont, 'Segoe UI', Oxygen, sans-serif"
        theme={currentTheme}
      >
        <div className="space-y-4">
          <p className="text-base-content/60 text-[15px]">
            Are you sure you want to delete this media?
          </p>
          <p className="text-base-content font-semibold text-[14px]">
            {deleteModal.itemDescription}
          </p>
          <p className="text-base-content/50 text-[13px]">
            This action cannot be undone.
          </p>

          <div className="flex gap-3 pt-2">
            <button
              onClick={() => setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' })}
              className="flex-1 py-3 px-4 bg-base-200 text-base-content font-semibold text-[15px] rounded-xl hover:bg-base-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                onDelete?.(deleteModal.itemId);
                setDeleteModal({ isOpen: false, itemId: null, itemDescription: '' });
              }}
              className="flex-1 py-3 px-4 bg-error text-error-content font-semibold text-[15px] rounded-xl hover:bg-error/90 transition-colors"
            >
              Delete
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Review;

Review.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      assignmentItemId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      type: PropTypes.string,
      description: PropTypes.string,
      required: PropTypes.bool,
      status: PropTypes.string,
    })
  ),
  capturedFiles: PropTypes.object,
  serverCompleted: PropTypes.object,
  completedItemsMedia: PropTypes.object,
  branding: PropTypes.shape({
    theme: PropTypes.string,
  }),
  workflow: PropTypes.shape({
    branding: PropTypes.shape({
      logoUrl: PropTypes.string,
    }),
  }),
  assignment: PropTypes.shape({
    expiresAt: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    submitterName: PropTypes.string,
    workflowName: PropTypes.string,
    customerReferenceNumber: PropTypes.string,
  }),
  submitError: PropTypes.string,
  onRetake: PropTypes.func,
  onDelete: PropTypes.func,
  onDeletePhoto: PropTypes.func,
  onAddPhoto: PropTypes.func,
  onSubmit: PropTypes.func,
  onStart: PropTypes.func,
  token: PropTypes.string,
  onOpenNotes: PropTypes.func,
  notes: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      note: PropTypes.string,
      timestamp: PropTypes.string,
    })
  ),
  onAddNote: PropTypes.func,
  syncBlockedNotice: PropTypes.bool,
  onDismissSyncBlockedNotice: PropTypes.func,
  syncStatus: PropTypes.shape({
    status: PropTypes.oneOf(['success', 'error']),
    message: PropTypes.string,
  }),
  onDismissSyncStatus: PropTypes.func,
};
